/**
 * Per-page help: maps a route to the Compendium chapter that explains it.
 *
 * The shell renders a small floating "?" affordance on every game page; this
 * map tells it which Compendium section that page links to. The link target is
 * `#/wiki?s=<slug>`, which the Wiki page reads to open + scroll to that chapter.
 * Slugs here must match the `section(slug, …)` ids in pages/Wiki.ts.
 */

interface HelpTarget {
  slug: string;
  title: string;
}

const ROUTE_HELP: Record<string, HelpTarget> = {
  '/training': { slug: 'training-yard', title: 'The Training Yard' },
  '/sparring': { slug: 'sparring-hall', title: 'The Sparring Hall' },
  '/attacks': { slug: 'attacks', title: 'Attacks (Player vs. Player)' },
  '/misdeeds': { slug: 'misdeeds', title: 'Misdeeds (Crime)' },
  '/good-deeds': { slug: 'good-deeds', title: 'Good Deeds' },
  '/scholarium': { slug: 'scholarium', title: 'The Scholarium' },
  '/trades': { slug: 'trades', title: 'Trades & Vocations' },
  '/forge': { slug: 'forge', title: 'The Forge' },
  '/apothecary': { slug: 'forge', title: 'The Forge' },
  '/cookfire': { slug: 'gathering', title: 'The Trades of the Land' },
  '/brewhouse': { slug: 'gathering', title: 'The Trades of the Land' },
  '/replays': { slug: 'replays', title: 'Replays' },
  '/market': { slug: 'market', title: 'The Market' },
  '/travel': { slug: 'travel', title: 'Travel & Ships' },
  '/domiciles': { slug: 'domiciles', title: 'Domiciles' },
  '/houses': { slug: 'houses', title: 'Houses' },
  '/awards': { slug: 'awards-titles', title: 'Awards & Titles' },
  '/expeditions': { slug: 'expeditions', title: 'Expeditions' },
  '/racing': { slug: 'racing', title: 'The Racing Yard' },
  '/banks': { slug: 'banks', title: 'Banks' },
  '/mail': { slug: 'social', title: 'Mail' },
  '/leaderboards': { slug: 'leaderboards', title: 'Leaderboards' },
  '/friends': { slug: 'social', title: 'Friends & Social' },
  '/daily': { slug: 'daily-tasks', title: 'Daily Tasks' },
  '/events': { slug: 'events', title: 'Events' },
  '/shoutbox': { slug: 'social', title: 'The Shoutbox' },
  '/inventory': { slug: 'inventory', title: 'Inventory & Equipment' },
  '/infirmary': { slug: 'infirmary-dungeon', title: 'The Infirmary & Dungeon' },
  '/dungeon': { slug: 'infirmary-dungeon', title: 'The Infirmary & Dungeon' },
  '/profile': { slug: 'your-character', title: 'Your Character' },
  '/membership': { slug: 'membership', title: 'Membership' },
  '/settings': { slug: 'settings', title: 'Settings & Account' },
};

/** The first path segment as a route key (e.g. '/mail/42' → '/mail'). */
function routeKeyFromHash(): string {
  let h = window.location.hash || '';
  if (h.startsWith('#')) h = h.slice(1);
  const q = h.indexOf('?');
  if (q >= 0) h = h.slice(0, q);
  const segs = h.split('/').filter(Boolean);
  return segs.length ? `/${segs[0]}` : '';
}

/** Help target for the current route, or null if the page has no chapter
 *  (e.g. the Compendium itself, public profiles, legal pages). */
export function helpForCurrentRoute(): HelpTarget | null {
  const key = routeKeyFromHash();
  if (key === '/wiki') return null; // already in the Compendium
  return ROUTE_HELP[key] ?? null;
}
